<template>
  <Teleport to="body">
  <div class="modal-wrap">
    <div class="modal" tabindex="-1">
      <h2 class="modal__text" v-if="title">{{title}}</h2>
      <div class="modal__buttons" ref="modalContent">
        <slot/>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref } from 'vue';

interface Props {
  title?: string
}

defineProps<Props>()

const modalContent = ref<HTMLElement | null>(null);

// Обработка нажатия клавиш Enter и Tab
const handleKeyboardKeys = (event: KeyboardEvent) => {
  if (!modalContent.value) return;

  const focusableElements = Array.from(
    modalContent.value.querySelectorAll<HTMLElement>('button, input, textarea, select, a[href], [tabindex]:not([tabindex="-1"])')
  ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);

  if (!focusableElements.length) return;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  // Проверяем, сфокусирован ли элемент внутри модалки
  if (event.key === 'Enter' && !modalContent.value.contains(document.activeElement)) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Ограничение фокуса внутри модального окна
  if (event.key === 'Tab') {
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }
}

onMounted(() => {
  // Наводим фокус если только одна кнопка в модальном окне
  if (modalContent.value) {
    const buttons = modalContent.value.querySelectorAll('button');
    if (buttons.length === 1) {
      const button = buttons[0];
      button.classList.add('focus-invisible');
      button.focus();
    }
  }
  document.addEventListener('keydown', handleKeyboardKeys);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyboardKeys);
});
</script>

<script lang="ts">
export default {
  name: 'vita-modal'
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '../../static/style/variables.scss' as *;
@use '../../static/style/mixins.scss' as *;

.modal-wrap {
  background: rgba(0, 0, 0, 0.4);
  -webkit-backdrop-filter: blur(2.5px);
  backdrop-filter: blur(2.5px);
  display: flex;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  z-index: 9999;
}

.modal {
  margin: auto;
  background: #fff;
  padding: 20px 24px;
  border-radius: 3px;
  border: 1px solid #DDDDDD;

  &:has(.dialog) {
    padding: 0;
    border: 0;
  }

  h2 {
    font-size: 14px;
    font-weight: 500;
    line-height: 17px;
    text-align: center;
    margin-bottom: 30px;
  }

  &__buttons {
    display: flex;
    justify-content: flex-end;
  }
}

.modal-close {
  position: absolute;
  top: 0;
  left: 0;
}
</style>
